import type {
    ClientSpecMapEntry,
    EditorClientSpecMapEntry,
    TPAWidget,
    DataItem,
    AppData,
    ClientSpecMap,
    AppDefinitionId,
    Pointer,
    CompRef
} from '@wix/document-services-types'
import _ from 'lodash'
import {TPA_CONSTANTS} from '@wix/document-manager-utils'
import type {CreateExtArgs, DalItem} from '@wix/document-manager-core'
import type {AppsInstallStateExtAPI} from '../appsInstallState'
import {getPagesList} from '../page/pageData'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import type {StructureExtensionAPI} from '../structure'
import type {TPAExtensionAPI} from '../tpa'
import type {DataExtensionAPI} from '../data'

const CONTROLLER_DATA_TYPE = 'AppController'

export const isAppRevoked = (appData: ClientSpecMapEntry) => _.get(appData, ['permissions', 'revoked'])
export const isAppExcludedFromAutoRevoke = (appData: ClientSpecMapEntry) =>
    _.get(appData, ['appFields', 'excludeFromAutoRevoke'])
export const isSection = (appData: EditorClientSpecMapEntry, widgetId: string) =>
    !!_.get(appData?.widgets, [widgetId, 'appPage'])

export const getTpaPageIdFromWidgetId = (appData: EditorClientSpecMapEntry, widgetId: string) =>
    _.get(appData?.widgets, [widgetId, 'appPage', 'id'])
export const isSectionInstalledByTpaPageId = (
    pagesData: Record<string, any>,
    appData: EditorClientSpecMapEntry,
    widgetId: string
) => {
    if (!isSection(appData, widgetId)) {
        throw new Error(`widgetId ${widgetId} is not section`)
    }
    const tpaPageId = getTpaPageIdFromWidgetId(appData, widgetId)
    return _.some(pagesData, {appDefinitionId: appData.appDefinitionId, tpaPageId})
}
export const computeHidePage = (widgetData: TPAWidget, isHidden: boolean | undefined, defaultValue: boolean) => {
    const hidePage = _.defaultTo(isHidden, _.get(widgetData, ['componentFields', 'hiddenInPagesPanel']))
    return _.defaultTo(hidePage, defaultValue)
}

export const isSectionInstalledByTpaPageIdPredicate = (appData: EditorClientSpecMapEntry, widgetId: string) => {
    if (!isSection(appData, widgetId)) {
        throw new Error(`widgetId ${widgetId} is not section`)
    }
    const tpaPageId = getTpaPageIdFromWidgetId(appData, widgetId)
    return (partialPageData: {appDefinitionId?: string; tpaPageId?: string}) =>
        partialPageData.appDefinitionId === appData.appDefinitionId && partialPageData.tpaPageId === tpaPageId
}
export const isMultiSectionInstanceEnabled = (appData: EditorClientSpecMapEntry, widgetId: string) => {
    if (!widgetId) {
        return false
    }
    const widgetData = _.get(appData, ['widgets', widgetId])
    return _.get(widgetData, ['appPage', 'multiInstanceEnabled'])
}
export const hasEditorPlatformPart = (appData: ClientSpecMapEntry) =>
    _.get(appData, ['appFields', 'platform', 'editorScriptUrl'])

const hasViewerPlatformPart = (appData: ClientSpecMapEntry) =>
    _.get(appData, ['appFields', 'platform', 'viewerScriptUrl'])

export const isAppPlatformEditorOnly = (appData: ClientSpecMapEntry) =>
    // @ts-ignore
    hasEditorPlatformPart(appData) && !hasViewerPlatformPart(appData) && _.isEmpty(appData.widgets)

export const getAppVersion = (appData: ClientSpecMapEntry) =>
    _.get(appData, ['appFields', 'semanticVersion']) ?? _.get(appData, ['appFields', 'installedVersion'])

export const isEditorOrHybridApp = (appData: ClientSpecMapEntry) => {
    const appType = _.get(appData, ['appType'])
    return appType === 'Hybrid' || appType === 'Editor'
}

export const isStudioApp = (appData: ClientSpecMapEntry) => _.has(appData, ['appFields', 'platform', 'studio'])

export const isAppControllerData = (dataItem: DataItem) => {
    return dataItem?.type === CONTROLLER_DATA_TYPE
}

export const isTpaByDataType = (dataType: string) => {
    return _.includes(TPA_CONSTANTS.DATA_TYPE, dataType)
}

export const isUnifiedComponentData = (dataType: string) => {
    return dataType === 'CustomElement'
}

export const isDashboardAppOnly = (appData: ClientSpecMapEntry) => {
    const appType = _.get(appData, ['appType'])
    return hasEditorPlatformPart(appData) && appType === 'Dashboard'
}

export const appHasRouter = (app: ClientSpecMapEntry, routerConfigMap: any) => {
    return hasEditorPlatformPart(app) && _.some(routerConfigMap, {appDefinitionId: _.get(app, ['appDefinitionId'])})
}

const isHybridAppFromAppData = (appData: AppData) => {
    if (_.isUndefined(appData) || _.isUndefined(appData.appType)) {
        return false
    }
    return appData.appType === 'Hybrid'
}

export const isPendingAppData = (appData: AppData) =>
    appData?.requiresEditorComponent && isHybridAppFromAppData(appData)

export const findAppByDefinitionId = (data: ClientSpecMap | undefined, targetAppDefId: string) => {
    return data
        ? Object.values(data).find(app => 'appDefinitionId' in app && app.appDefinitionId === targetAppDefId)
        : data
}

const getMainWidgetId = (createExtArgs: CreateExtArgs, appDefinitionId: AppDefinitionId) => {
    const {dal, pointers} = createExtArgs
    const appDataPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
    const appData = dal.get(appDataPointer)
    if (appData) {
        const widgetData = _.find(appData.widgets, {default: true})
        return _.get(widgetData, ['widgetId'])
    }
    return undefined
}

export const isLastInstalledMainWidget = (
    createExtArgs: CreateExtArgs,
    installedWidgets: any,
    appDefinitionId: AppDefinitionId,
    compPointer: Pointer
) => {
    const installedMainWidgets = _.filter(installedWidgets, {widgetId: getMainWidgetId(createExtArgs, appDefinitionId)})
    return installedMainWidgets && installedMainWidgets.length === 1 && installedMainWidgets[0].id === compPointer.id
}

const getTpaChildComponents = (createExtArgs: CreateExtArgs, parentCompPointer: CompRef): CompRef[] => {
    const {pointers, extensionAPI} = createExtArgs
    const {components, tpa} = extensionAPI as StructureExtensionAPI & TPAExtensionAPI

    const childrenPointerArr = pointers.components.getChildrenRecursively(parentCompPointer)

    return childrenPointerArr.filter(childComp => {
        const componentType = components.getComponentType(childComp)
        return tpa.isTpaByCompType(componentType)
    })
}

type TpaComp = DalItem & {id: string; pageId: string}
const getAllTpaCompsOnPage = (createExtArgs: CreateExtArgs, pageId: string): TpaComp[] => {
    const {viewer, data} = createExtArgs.extensionAPI as ViewerExtensionAPI & DataExtensionAPI
    const currentViewMode = viewer.getCurrentViewMode()
    const pagePointer = createExtArgs.pointers.structure.getPage(pageId, currentViewMode)
    const tpaCompPointers = getTpaChildComponents(createExtArgs, pagePointer)

    return tpaCompPointers.map(tpaCompPointer => ({
        ...data.get(tpaCompPointer),
        id: tpaCompPointer.id,
        pageId
    }))
}

export const getWidgetsByAppDefId = (createExtArgs: CreateExtArgs, appDefinitionId: AppDefinitionId): TpaComp[] => {
    let widgets: any = []
    const pagesIds = getPagesList(createExtArgs, true, true)
    pagesIds.forEach(pageId => {
        const tpaCompsOnPage = getAllTpaCompsOnPage(createExtArgs, pageId)
        widgets = _.union(
            widgets,
            _.filter(
                tpaCompsOnPage,
                // eslint-disable-next-line lodash/matches-shorthand
                tpaComp =>
                    tpaComp.appDefinitionId === appDefinitionId && tpaComp.type === TPA_CONSTANTS.DATA_TYPE.TPA_WIDGET
            )
        )
    })

    return widgets.length === 0 ? null : widgets
}

export const isAppActive = (createExtArgs: CreateExtArgs, appData: ClientSpecMapEntry): boolean => {
    if (isDashboardAppOnly(appData)) {
        return !isAppRevoked(appData)
    }

    if (!('appDefinitionId' in appData)) {
        return false
    }

    const {appsInstallState} = createExtArgs.extensionAPI as AppsInstallStateExtAPI
    return appsInstallState.isAppInstalled(appData.appDefinitionId)
}
